import {createAsyncThunk, createSlice} from "@reduxjs/toolkit"
import {loadWorkspace} from "./workspaceSlice"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"
import axios from "axios"
import {deleteArticle} from "./deletedArticlesSlice"
import cloneDeep from "lodash/cloneDeep"

export const makeNewOneTimeSearch = createAsyncThunk(
	"articleSearchQuery/makeNewOneTimeSearch",
	async ({ query, source, workspaceId }, {rejectWithValue}) => {
		if (!query) {
			return rejectWithValue("Missing search query")
		}

		const { data } = await axios.get(
			`/api/workspace/${workspaceId}
			/article?search=${query}
			&page=1
			&imported=1
			&pubmed=${source === "pubmed" ? 1 : 0}`
		)
		const { count, articles, page, reachable, hasMore } = data

		if (count === 0) {
			return rejectWithValue("This search returned no results, check your query and try again")
		}

		return {
			count,
			page,
			articles,
			workspaceId,
			reachable,
			hasMore
		}
	}
)

export const paginateOneTimeSearch = createAsyncThunk(
	"articleSearchQuery/paginateOneTimeSearch",
	async ({ workspaceId }, { getState }) => {
		const { page, query, source } = selectArticleSearchQuery(getState(), workspaceId)

		const {data} = await axios.get(
			`/api/workspace/${workspaceId}
			/article?search=${query}
			&page=${page}
			&imported=1
			&pubmed=${source === "pubmed" ? 1 : 0}`
		)

		return {
			workspaceId,
			articles: data.articles,
			page: data.page,
			count: data.count,
			hasMore: data.hasMore,
			reachable: data.reachable,
		}
	},
	{
		condition: ({workspaceId}, {getState}) =>
			!selectArticleSearchQuery(getState(), workspaceId).isPaginating
	}
)

/**
 * an article returned from our server can either be raw citation data from Zotero
 * or existing {Article} data from our server. The difference is where the value of
 * PMID is located. This convenience function normalizes an Article key regardless of source
 * @param article
 * @returns {string}
 */
const getArticleKey = (article) => article.PMID ?? article.article.PMID

/**
 * for reference, what the server is returning:
 * SearchDTO = {
 *     articles: [{Article | RawZoteroTranslation}],
 *     count: number,
 *     filteredCount?: number
 *     page: number,
 *     hasMore: boolean,
 *     reachable?: number,
 * }
 */
const initialState = {
	articles: [],
	page: 1,
	count: 0,
	reachable: 0,
	hasMore: true,
	query: "",
	source: "pubmed",
	isLoading: false,
	isPaginating: false,
	error: null,
}
const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, initialState)

const articleSearchQuery = createSlice({
	name: "searchedArticles",
	initialState: {
		byWorkspace: {}
	},
	reducers: {
		setArticles: (state, action) => {
			const {articles, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.articles = articles
		},
		resetArticleSearchQuery: (state, action) => {
			const workspaceId = action.payload
			state.byWorkspace[workspaceId] = cloneDeep(initialState)
		},
		setResults: (state, action) => {
			const { workspaceId, count, articles, page, reachable, hasMore } = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			//de-dupe just incase...
			const seen = new Set(workspace.articles.map(getArticleKey))
			workspace.articles.push(...articles.filter(a => !seen.has(getArticleKey(a))))

			workspace.page = page
			workspace.count = count
			workspace.reachable = reachable
			workspace.hasMore = hasMore
			workspace.isLoading = false
			workspace.isPaginating = false
		},
		clearOneTimeSearchError: (state, action) => {
			const workspace = ensureWorkspace(state, action.payload)
			workspace.error = initialState.error
		},
		updateArticleAsImported: (state, action) => {
			const { workspaceId } = action.payload
			const workspace = ensureWorkspace(state, workspaceId)

			const index = workspace.articles.findIndex((a) => {
				const articleIsDeleted = a?.articleId && a?.deletedAt !== null
				if (articleIsDeleted) {
					return a?.article?.DOI === action.payload?.article?.DOI || action.payload?.article?.PMID === a?.article?.PMID
				}
				return a?.DOI === action.payload?.article?.DOI || action.payload?.article?.PMID === a?.PMID
			})

			if (index > -1) {
				workspace.articles[index] = action.payload
			}
		},
		updateArticlesAsDeleted: (state, action) => {
			const { workspaceId, article } = action.payload
			const {articleId, deletedAt} = article
			const workspace = ensureWorkspace(state, workspaceId)
			const index = workspace.articles.findIndex(a => a.articleId === articleId)
			if (index > -1) {
				workspace.articles[index].deletedAt = deletedAt
			}
		}
	},
	extraReducers: (builder) => (
		builder
			.addCase (
				loadWorkspace.fulfilled,
				(state, action) => {
					const workspaceId = action.payload.workspace.id
					articleSearchQuery.caseReducers.resetArticleSearchQuery(state, {payload: workspaceId})
				}
			)
			.addCase(makeNewOneTimeSearch.pending, (state, action) => {
				const { workspaceId, query, source } = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.articles = []
				workspace.page = initialState.page
				workspace.count = initialState.count
				workspace.reachable = initialState.reachable
				workspace.hasMore = initialState.hasMore
				workspace.error = null
				workspace.query = query
				workspace.source = source
				workspace.isLoading = true
			})
			.addCase(makeNewOneTimeSearch.rejected, (state, action) => {
				const { workspaceId, source } = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.source = source
				workspace.error = action.payload
				workspace.query = initialState.query
				workspace.isLoading = false
				console.error("failed to make new one time search: ", action.payload)
			})
			.addCase(
				makeNewOneTimeSearch.fulfilled,
				articleSearchQuery.caseReducers.setResults
			)
			.addCase(paginateOneTimeSearch.pending, (state, action) => {
				const { workspaceId } = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.isPaginating = true
			})
			.addCase(paginateOneTimeSearch.rejected, (state, action) => {
				const { workspaceId } = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.isPaginating = false
				workspace.error = action.error?.payload
				console.error("failed to paginate existing one time search: ", action.error)
			})
			.addCase(
				paginateOneTimeSearch.fulfilled,
				articleSearchQuery.caseReducers.setResults
			)
			.addCase(
				deleteArticle.fulfilled,
				articleSearchQuery.caseReducers.updateArticlesAsDeleted
			)
	)
})

export const { setArticles, updateArticleAsImported, clearOneTimeSearchError, resetArticleSearchQuery } = articleSearchQuery.actions

export const selectArticleSearchQuery = (state, workspaceId) =>
	state.articleSearchQuery.byWorkspace?.[workspaceId] ?? initialState
export default articleSearchQuery.reducer
